import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:    <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:   <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    Star:         <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    CheckCircle:  <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Shield:       <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    Award:        <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></>,
    Lock:         <><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    ThumbsUp:     <><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88z"/></>,
    AlertCircle:  <><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></>,
    MessageCircle:<><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></>,
    Filter:       <><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></>,
    Sparkles:     <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/></>,
    MapPin:       <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Calendar:     <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Star renderer ── */
function Stars({ rating, size = 14 }) {
  return (
    <div style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i <= rating ? T.amber : T.n150} stroke="none">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </div>
  );
}

/* ── Avatar initials ── */
function Avatar({ name, size = 40 }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("");
  const colors = [
    { bg: T.g50,      text: T.g700,   border: T.g100 },
    { bg: "#eff6ff",  text: "#1d4ed8", border: "#bfdbfe" },
    { bg: "#fdf4ff",  text: "#7e22ce", border: "#e9d5ff" },
    { bg: "#fff7ed",  text: "#c2410c", border: "#fed7aa" },
    { bg: "#f0fdf4",  text: "#15803d", border: "#bbf7d0" },
  ];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: color.bg, border: `1.5px solid ${color.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 600, color: color.text,
      fontFamily: T.sans, letterSpacing: "0.02em",
    }}>
      {initials}
    </div>
  );
}

/* ── Sample reviews data ── */
const REVIEWS = [
  {
    id: 1, name: "Amina W.", location: "Kilimani, Nairobi",
    service: "Deep house cleaning", pro: "Charles M.", rating: 5,
    date: "June 2026",
    text: "I was honestly not sure what to expect booking a cleaner through an app but this completely changed my mind. Charles arrived on time, was professional throughout, and left my apartment looking better than it has in months. The whole process from booking to payment was seamless. I have already rebooked for next month.",
    verified: true,
  },
  {
    id: 2, name: "Brian O.", location: "South B, Nairobi",
    service: "Electrical fault diagnosis", pro: "James K.", rating: 5,
    date: "May 2026",
    text: "Had a persistent electrical issue that two other electricians could not properly diagnose. James came, identified the problem within 20 minutes, explained it clearly, fixed it, and showed me exactly what had been wrong. Pricing was exactly what was quoted. That transparency alone made the difference.",
    verified: true,
  },
  {
    id: 3, name: "Fatuma S.", location: "Nyali, Mombasa",
    service: "Bathroom plumbing repair", pro: "Ali H.", rating: 5,
    date: "May 2026",
    text: "Booked Ali for a leaking pipe under the bathroom sink that had been causing problems for weeks. He responded fast, showed up the next morning, and sorted it within the hour. Clean, efficient, no mess left behind. This is the kind of service I have been looking for in Mombasa for a long time.",
    verified: true,
  },
  {
    id: 4, name: "David M.", location: "Karen, Nairobi",
    service: "Garden maintenance", pro: "Peter N.", rating: 5,
    date: "April 2026",
    text: "Peter has now done our garden three times and each time it is better than the last. He understands what we want without us having to explain in detail, takes real pride in the work, and is always on time. My wife specifically asked me to make sure we keep using him. That says enough.",
    verified: true,
  },
  {
    id: 5, name: "Grace N.", location: "Westlands, Nairobi",
    service: "Interior painting", pro: "Samuel O.", rating: 4,
    date: "April 2026",
    text: "Samuel did a great job on our living room and two bedrooms. The finish is clean and the colour came out exactly as we discussed. He took slightly longer than estimated but kept us updated the whole time and the quality of the work is genuinely impressive. Would use him again for the rest of the house.",
    verified: true,
  },
  {
    id: 6, name: "Mohamed A.", location: "Milimani, Kisumu",
    service: "Appliance repair", pro: "Robert O.", rating: 5,
    date: "March 2026",
    text: "My washing machine had stopped working and I assumed I would need to buy a new one. Robert diagnosed it quickly, sourced the part, came back the next day and had it running again. Saved me a significant amount of money. Genuinely skilled and honest about what the problem was and what it would cost.",
    verified: true,
  },
  {
    id: 7, name: "Lilian K.", location: "Lavington, Nairobi",
    service: "Deep house cleaning", pro: "Mary W.", rating: 5,
    date: "March 2026",
    text: "Mary is exceptional. I have used cleaning services before but the attention to detail here was on another level. She cleaned areas I did not even ask about and left the place spotless. The JiraniApp booking process is also just really easy to use. I had everything confirmed in under five minutes.",
    verified: true,
  },
  {
    id: 8, name: "James T.", location: "Nakuru CBD, Nakuru",
    service: "Security system installation", pro: "Daniel M.", rating: 4,
    date: "February 2026",
    text: "Daniel installed a full camera system at our business premises. He was knowledgeable, walked us through everything after installation, and made sure we could access the cameras remotely before he left. A couple of small things needed adjusting the next day and he came back without any fuss. Good professional.",
    verified: true,
  },
  {
    id: 9, name: "Winnie A.", location: "Bamburi, Mombasa",
    service: "Carpentry and furniture repair", pro: "Joseph K.", rating: 5,
    date: "February 2026",
    text: "Joseph repaired three pieces of furniture I thought were beyond saving. The craftsmanship is genuinely impressive and he clearly enjoys the work. He also suggested a better approach than what I had originally described and he was right. One of those professionals you are glad you found.",
    verified: true,
  },
  {
    id: 10, name: "Kevin M.", location: "Kasarani, Nairobi",
    service: "Electrical installation", pro: "Patrick W.", rating: 5,
    date: "January 2026",
    text: "Patrick wired up additional sockets and a new light fitting in our home office. He worked neatly, explained what he was doing at each stage, and cleaned up completely when done. The price was fair and exactly what was agreed upfront. No surprises. Highly recommend for any electrical work.",
    verified: true,
  },
  {
    id: 11, name: "Aisha M.", location: "Kondele, Kisumu",
    service: "House cleaning", pro: "Faith A.", rating: 5,
    date: "January 2026",
    text: "Booked Faith for a one-off deep clean before a family event. She was thorough, respectful of the space, and incredibly efficient. The house was ready well before we needed it. I was hesitant to try a new service but everything about the experience, from booking to payment, was reassuring.",
    verified: true,
  },
  {
    id: 12, name: "Stephen O.", location: "Embakasi, Nairobi",
    service: "Plumbing installation", pro: "George N.", rating: 4,
    date: "December 2025",
    text: "George installed a new water heater and sorted out some old pipe connections that were causing low pressure. He was honest about what needed replacing and what did not, which I really appreciated. The work is solid and the pressure issue is completely resolved. Would book again for future plumbing work.",
    verified: true,
  },
];

const SERVICES = ["All services", "Cleaning", "Plumbing", "Electrical", "Painting", "Gardening", "Carpentry", "Appliance repair", "Security"];
const RATINGS  = ["All ratings", "5 stars", "4 stars", "3 stars"];
const CITIES   = ["All cities", "Nairobi", "Mombasa", "Kisumu", "Nakuru"];

/* ── Review card ── */
function ReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > 220;
  const displayText = !expanded && isLong ? review.text.slice(0, 220).trim() + "..." : review.text;

  return (
    <div style={{
      background: T.white, borderRadius: 16,
      border: `1px solid ${T.n150}`,
      padding: "22px 24px",
      display: "flex", flexDirection: "column", gap: 14,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={review.name} size={42} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.n900, lineHeight: 1.3 }}>{review.name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
              <Icon name="MapPin" size={11} color={T.n400} strokeWidth={2} />
              <span style={{ fontSize: 12, color: T.n400 }}>{review.location}</span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
          <Stars rating={review.rating} size={13} />
          <span style={{ fontSize: 11, color: T.n400 }}>{review.date}</span>
        </div>
      </div>

      {/* Review text */}
      <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: 0, fontStyle: "italic" }}>
        "{displayText}"
      </p>
      {isLong && (
        <button onClick={() => setExpanded(e => !e)} style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 13, fontWeight: 600, color: T.g600, fontFamily: T.sans,
          padding: 0, textAlign: "left",
        }}>
          {expanded ? "Show less" : "Read full review"}
        </button>
      )}

      {/* Footer */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 12, borderTop: `1px solid ${T.n100}`, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: T.n400 }}>Service:</span>
          <span style={{
            fontSize: 12, fontWeight: 500, color: T.g700,
            background: T.g50, border: `1px solid ${T.g100}`,
            borderRadius: 99, padding: "2px 10px",
          }}>{review.service}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {review.verified && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Icon name="CheckCircle" size={13} color={T.g500} strokeWidth={2} />
              <span style={{ fontSize: 11, fontWeight: 600, color: T.g600, letterSpacing: "0.04em" }}>Verified booking</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Info row ── */
function InfoRow({ icon, title, desc }) {
  return (
    <div style={{ display: "flex", gap: 16, padding: "18px 0", borderBottom: `1px solid ${T.n100}` }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={icon} size={16} color={T.g500} strokeWidth={1.7} />
      </div>
      <div style={{ flex: 1, paddingTop: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 14, color: T.n600, lineHeight: 1.65 }}>{desc}</div>
      </div>
    </div>
  );
}

/* ── Rating bar ── */
function RatingBar({ stars, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 12, color: T.n400, width: 36, textAlign: "right", flexShrink: 0 }}>{stars} star</span>
      <div style={{ flex: 1, height: 6, borderRadius: 99, background: T.n100, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 99, background: pct > 60 ? T.g400 : pct > 30 ? T.amber : T.n300, transition: "width .4s ease" }} />
      </div>
      <span style={{ fontSize: 12, color: T.n400, width: 28, flexShrink: 0 }}>{pct}%</span>
    </div>
  );
}

/* ══ Main page ══ */
export default function CustomerReviewsPage() {
  useFonts();
  const navigate = useNavigate();

  const [serviceFilter, setServiceFilter] = useState("All services");
  const [ratingFilter,  setRatingFilter]  = useState("All ratings");
  const [cityFilter,    setCityFilter]    = useState("All cities");

  const filtered = REVIEWS.filter(r => {
    const svcMatch  = serviceFilter === "All services" || r.service.toLowerCase().includes(serviceFilter.toLowerCase());
    const rateMatch = ratingFilter  === "All ratings"  || r.rating === parseInt(ratingFilter);
    const cityMatch = cityFilter    === "All cities"   || r.location.includes(cityFilter);
    return svcMatch && rateMatch && cityMatch;
  });

  const avgRating  = (REVIEWS.reduce((s, r) => s + r.rating, 0) / REVIEWS.length).toFixed(1);
  const totalCount = REVIEWS.length;
  const fiveStars  = REVIEWS.filter(r => r.rating === 5).length;
  const fourStars  = REVIEWS.filter(r => r.rating === 4).length;

  const FilterSelect = ({ value, onChange, options }) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      padding: "9px 32px 9px 12px", borderRadius: 9,
      border: `1.5px solid ${T.n150}`, background: T.white,
      fontSize: 13, fontFamily: T.sans, color: T.n700,
      cursor: "pointer", outline: "none",
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238a93a8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
    }}
    onFocus={e => (e.target.style.borderColor = T.g500)}
    onBlur={e => (e.target.style.borderColor = T.n150)}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`* { box-sizing: border-box; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}` }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = T.n800)}
          onMouseLeave={e => (e.currentTarget.style.color = T.n400)}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
      </div>

      {/* ── Hero ── */}
      <div style={{ padding: "clamp(48px,7vw,88px) 20px clamp(40px,5vw,60px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 20 }}>
            <Icon name="Star" size={13} color={T.g500} strokeWidth={2.2} />
            Customer reviews
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,62px)", fontWeight: 400, margin: "0 0 18px", lineHeight: 1.07, color: T.n900, letterSpacing: "-0.03em", maxWidth: 580 }}>
            What clients say about{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>JiraniApp</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 0", maxWidth: 520 }}>
            Every review on this page comes from a verified booking. Real clients, real jobs,
            real experiences. No anonymous submissions, no unverified ratings.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ── Rating summary ── */}
        <div style={{ paddingTop: "clamp(36px,5vw,52px)", display: "flex", flexWrap: "wrap", gap: 20, marginBottom: "clamp(40px,6vw,56px)", alignItems: "flex-start" }}>

          {/* Big number */}
          <div style={{ textAlign: "center", padding: "24px 32px", background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 18, minWidth: 140 }}>
            <div style={{ fontFamily: T.serif, fontSize: "clamp(48px,8vw,64px)", fontWeight: 400, color: T.n900, lineHeight: 1, letterSpacing: "-0.04em" }}>{avgRating}</div>
            <Stars rating={5} size={16} />
            <div style={{ fontSize: 13, color: T.n400, marginTop: 8 }}>out of 5</div>
            <div style={{ fontSize: 12, color: T.g600, fontWeight: 600, marginTop: 4 }}>{totalCount} reviews</div>
          </div>

          {/* Breakdown bars */}
          <div style={{ flex: "1 1 200px", padding: "20px 0" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n700, marginBottom: 14 }}>Rating breakdown</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <RatingBar stars={5} count={fiveStars} total={totalCount} />
              <RatingBar stars={4} count={fourStars} total={totalCount} />
              <RatingBar stars={3} count={0} total={totalCount} />
              <RatingBar stars={2} count={0} total={totalCount} />
              <RatingBar stars={1} count={0} total={totalCount} />
            </div>
          </div>

          {/* Highlights */}
          <div style={{ flex: "1 1 160px", display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "Verified bookings", value: "100%" },
              { label: "Would rebook",      value: "94%" },
              { label: "On-time arrival",   value: "91%" },
            ].map(s => (
              <div key={s.label} style={{ padding: "14px 16px", borderRadius: 12, border: `1px solid ${T.n150}`, background: T.n50 }}>
                <div style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, letterSpacing: "-0.03em", lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 12, color: T.n400, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── How the review system works ── */}
        <div style={{ marginBottom: "clamp(40px,6vw,56px)" }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              How reviews work
            </span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            A system built for <em style={{ fontStyle: "italic", color: T.g600 }}>trust</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            Reviews are only as useful as they are honest. Here is how we keep them that way.
          </p>
          <div>
            <InfoRow
              icon="Lock"
              title="Verified bookings only"
              desc="You can only leave a review after a booking has been completed and confirmed through JiraniApp. We do not allow open or anonymous reviews. Every review is tied to a real job that actually happened."
            />
            <InfoRow
              icon="Calendar"
              title="Reviews open after job completion"
              desc="The review window opens once a job is marked complete. Clients have up to 7 days to leave a rating and written review. This gives enough time for a considered response, not just an immediate reaction."
            />
            <InfoRow
              icon="Star"
              title="Star rating plus written review"
              desc="Every review includes a star rating from 1 to 5 and space for written feedback. Both are required. A star rating without context tells you very little. The written review is where the real information lives."
            />
            <InfoRow
              icon="Shield"
              title="Moderated for honesty"
              desc="Our team reviews flagged submissions and removes anything that contains abusive, defamatory, or fraudulent content. Professionals can flag a review if they believe it is inaccurate, and we investigate every flag fairly."
            />
            <InfoRow
              icon="ThumbsUp"
              title="Professionals can respond"
              desc="JiraniPros are able to post a public response to any review they receive. This gives context to both sides and holds everyone accountable. You can read both the review and the response before booking."
            />
            <InfoRow
              icon="AlertCircle"
              title="No incentivised reviews"
              desc="JiraniApp does not allow professionals to offer discounts, refunds, or any other incentives in exchange for positive reviews. Reviews must reflect the genuine experience of the client, uninfluenced."
            />
          </div>
        </div>

        {/* ── Reviews grid with filters ── */}
        <div>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
                Client reviews
              </span>
              <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: 0, letterSpacing: "-0.03em", lineHeight: 1.2 }}>
                From real <em style={{ fontStyle: "italic", color: T.g600 }}>clients</em>
              </h2>
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 24, padding: "16px", borderRadius: 12, background: T.n50, border: `1px solid ${T.n100}`, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.n400, fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginRight: 4 }}>
              <Icon name="Filter" size={13} strokeWidth={2} />
              Filter
            </div>
            <FilterSelect value={serviceFilter} onChange={setServiceFilter} options={SERVICES} />
            <FilterSelect value={ratingFilter}  onChange={setRatingFilter}  options={RATINGS} />
            <FilterSelect value={cityFilter}    onChange={setCityFilter}    options={CITIES} />
            {(serviceFilter !== "All services" || ratingFilter !== "All ratings" || cityFilter !== "All cities") && (
              <button onClick={() => { setServiceFilter("All services"); setRatingFilter("All ratings"); setCityFilter("All cities"); }}
                style={{ padding: "9px 14px", borderRadius: 9, border: `1px solid ${T.n200}`, background: T.white, fontSize: 12, fontWeight: 600, color: T.n600, cursor: "pointer", fontFamily: T.sans }}>
                Clear filters
              </button>
            )}
            <span style={{ fontSize: 12, color: T.n400, marginLeft: "auto" }}>
              {filtered.length} {filtered.length === 1 ? "review" : "reviews"}
            </span>
          </div>

          {/* Cards */}
          {filtered.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
              {filtered.map(r => <ReviewCard key={r.id} review={r} />)}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "48px 20px", color: T.n400 }}>
              <Icon name="MessageCircle" size={32} color={T.n300} strokeWidth={1.4} />
              <p style={{ fontSize: 15, margin: "12px 0 0" }}>No reviews match these filters. Try adjusting your selection.</p>
            </div>
          )}
        </div>

        {/* ── CTA ── */}
        <div style={{ marginTop: "clamp(48px,7vw,72px)", padding: "clamp(32px,5vw,48px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50 }}>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Ready to book a{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>JiraniPro?</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Join hundreds of clients across Kenya who trust JiraniApp for their home services.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => navigate("/search")}
              style={{
                padding: "13px 28px", background: T.g500, color: T.white,
                border: "none", borderRadius: 11, fontSize: 14, fontWeight: 600,
                fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
                display: "inline-flex", alignItems: "center", gap: 8,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
              onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
              Find a professional
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button
              onClick={() => navigate("/how-it-works")}
              style={{
                padding: "13px 28px", background: "transparent", color: T.n700,
                border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 14, fontWeight: 600,
                fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = T.n400)}
              onMouseLeave={e => (e.currentTarget.style.borderColor = T.n200)}>
              How JiraniApp works
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}